import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  DescriptionList,
  Select,
  Textarea,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../../../design-system/components';
import { formatDateTime } from '../../../lib/formatters';
import {
  admin_applications_path,
  approve_admin_application_path,
  deny_admin_application_path,
} from '../../../lib/routes';
export default function Show({ affiliate, answers, campaigns }) {
  const [actionDialog, setActionDialog] = useState(null);
  const [denialReason, setDenialReason] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const isPending = affiliate.status === 'pending';
  const defaultCampaign = campaigns.find((c) => c.is_default);
  function openApprove() {
    setActionDialog('approve');
    if (defaultCampaign) {
      setCampaignId(String(defaultCampaign.id));
    }
  }
  function executeApprove() {
    setActionLoading(true);
    const data = {};
    if (campaignId) {
      data.campaign_id = campaignId;
    }
    router.post(approve_admin_application_path(affiliate.id), data, {
      onFinish: () => {
        setActionLoading(false);
        setActionDialog(null);
        setCampaignId('');
      },
    });
  }
  function executeDeny() {
    setActionLoading(true);
    router.post(
      deny_admin_application_path(affiliate.id),
      {
        denial_reason: denialReason,
      },
      {
        onFinish: () => {
          setActionLoading(false);
          setActionDialog(null);
          setDenialReason('');
        },
      },
    );
  }
  const details = [
    {
      term: 'Name',
      detail: affiliate.name,
    },
    {
      term: 'Email',
      detail: affiliate.email,
    },
    {
      term: 'Status',
      detail: (
        <Badge variant="warning" size="sm">
          {affiliate.status}
        </Badge>
      ),
    },
    {
      term: 'Submitted',
      detail: formatDateTime(affiliate.created_at),
    },
  ];
  const actionButtons = (
    <div className="flex items-center gap-2">
      {isPending && (
        <>
          <Button size="sm" variant="secondary" onClick={openApprove}>
            Approve
          </Button>
          <Button size="sm" variant="danger" onClick={() => setActionDialog('deny')}>
            Deny
          </Button>
        </>
      )}
    </div>
  );
  return (
    <AdminLayout>
      <Breadcrumbs
        items={[
          { label: 'Applications', href: admin_applications_path() },
          { label: affiliate.name },
        ]}
      />
      <PageHeader title={affiliate.name} description={affiliate.email} actions={actionButtons} />

      <div className="mt-6 space-y-6">
        {/* Applicant Details */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Applicant Details</h3>
          </CardHeader>
          <CardBody>
            <DescriptionList items={details} columns={2} />
          </CardBody>
        </Card>

        {/* Application Answers */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Application Answers</h3>
          </CardHeader>
          <CardBody>
            {answers.length > 0 ? (
              <dl className="space-y-4">
                {answers.map((answer) => (
                  <div key={answer.id}>
                    <dt className="text-sm font-medium text-fg-secondary">{answer.question_text}</dt>
                    <dd className="mt-1 text-sm text-fg">
                      {answer.answer_text || (
                        <span className="italic text-fg-tertiary">No answer provided</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-fg-tertiary">No application answers on file.</p>
            )}
          </CardBody>
        </Card>

        {/* Review Decision - only for pending */}
        {isPending && (
          <Card>
            <CardHeader>
              <h3 className="text-sm font-semibold text-fg">Review Decision</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <Select
                  label="Assign to Campaign"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                >
                  <option value="">Use default campaign</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.is_default ? ' (Default)' : ''}
                    </option>
                  ))}
                </Select>

                <div className="flex gap-3 border-t border-edge-strong pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      openApprove();
                    }}
                  >
                    Approve Application
                  </Button>
                  <Button variant="danger" onClick={() => setActionDialog('deny')}>
                    Deny Application
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Approve modal with campaign selection */}
      {actionDialog === 'approve' && (
        <Modal
          open
          onClose={() => {
            setActionDialog(null);
            setCampaignId('');
          }}
          size="sm"
        >
          <ModalTitle>Approve {affiliate.name}</ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              This affiliate will be approved and assigned to the selected campaign.
            </p>
            <Select
              label="Campaign"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
            >
              <option value="">Use default campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.is_default ? ' (Default)' : ''}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActionDialog(null);
                setCampaignId('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button size="sm" variant="outline" onClick={executeApprove} loading={actionLoading}>
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Deny modal with reason */}
      {actionDialog === 'deny' && (
        <Modal
          open
          onClose={() => {
            setActionDialog(null);
            setDenialReason('');
          }}
          size="sm"
        >
          <ModalTitle>
            <span className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-danger-500" aria-hidden="true" />
              Deny Application
            </span>
          </ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              Are you sure you want to deny {affiliate.name}&apos;s application?
            </p>
            <Textarea
              label="Denial Reason (optional)"
              value={denialReason}
              onChange={(e) => setDenialReason(e.target.value)}
              placeholder="Provide a reason for denial..."
              rows={3}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActionDialog(null);
                setDenialReason('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={executeDeny} loading={actionLoading}>
              Deny
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </AdminLayout>
  );
}
